import { useAssistantApi, useAssistantState } from '@assistant-ui/react'
import { MessageSquarePlus } from 'lucide-react'
import { type FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '#app/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'
import { trackUIEvent } from '#app/utils/plausible.client'
import { useOnceDismissed } from '#app/utils/use-once-dismissed'

type Props = {
	onNewChat: () => void
}

/**
 * Renders a floating "Ny konversation" button via React portal into the
 * #new-chat-slot div in the top-left of the chat area (see thread.tsx).
 * Living inside AssistantRuntimeProvider lets it read thread state
 * (isEmpty/isRunning) while rendering visually inside the thread root.
 * Hidden entirely when the thread is empty — nothing to reset.
 */
export const NewChatButton: FC<Props> = ({ onNewChat }) => {
	const api = useAssistantApi()
	const isEmpty = useAssistantState(({ thread }) => thread.isEmpty)
	const isRunning = useAssistantState(({ thread }) => thread.isRunning)
	const [open, setOpen] = useState(false)
	const [slot, setSlot] = useState<HTMLElement | null>(null)
	const [confirmSeen, markConfirmSeen] = useOnceDismissed('newChatConfirmSeen')

	useEffect(() => {
		setSlot(document.getElementById('new-chat-slot'))
	}, [])

	// Button is hidden entirely when the thread is empty — nothing to reset,
	// so a disabled button in the floating corner would just be visual noise.
	// Users can reset mid-stream if they realize they asked the wrong question
	// — we cancel the run first so assistant-ui aborts the fetch through its
	// own pathway, avoiding a stray "BodyStreamBuffer was aborted" rejection
	// on unmount.

	const handleConfirm = () => {
		trackUIEvent({
			eventName: 'button_click',
			label: 'new_chat_confirmed',
			// NOTE: location is hard-coded because NewChatButton is only rendered in the
			// 'full' ChatPage variant (see chat-page.tsx). If this button is ever surfaced
			// in embed mode, thread ChatVariant through and mirror TrackQuestionAsked's
			// location logic.
			location: 'assistent_start_page',
		})
		setOpen(false)
		markConfirmSeen()

		if (isRunning) {
			// Resetting mid-stream aborts the in-flight fetch. Depending on
			// *when* the abort fires, the rejection surfaces as one of two
			// Chromium messages that both escape assistant-ui's internal
			// iteration as unhandled rejections:
			//   - "The user aborted a request."   (abort before body read)
			//   - "BodyStreamBuffer was aborted." (abort during body read)
			// Swallow *these specific strings only* for a short window.
			// Do NOT broaden to AbortError.name or /aborted/i — that would
			// hide unrelated fetch aborts (analytics, prefetches, etc.).
			const BENIGN_RESET_MESSAGES = [
				'The user aborted a request.',
				'BodyStreamBuffer was aborted',
			]
			const handler = (event: PromiseRejectionEvent) => {
				const msg =
					typeof event.reason === 'string'
						? event.reason
						: (event.reason?.message ?? '')
				if (BENIGN_RESET_MESSAGES.some(m => msg.includes(m))) {
					event.preventDefault()
				}
			}
			window.addEventListener('unhandledrejection', handler)
			setTimeout(() => {
				window.removeEventListener('unhandledrejection', handler)
			}, 2000)

			api.thread().cancelRun()
		}
		onNewChat()
	}

	if (!slot || isEmpty) return null

	return createPortal(
		<>
			<Button
				variant="outline"
				size="sm"
				onClick={() => {
					if (confirmSeen) {
						handleConfirm()
					} else {
						setOpen(true)
					}
				}}
				className="whitespace-nowrap bg-background/80 shadow-sm backdrop-blur dark:bg-background/60"
			>
				<MessageSquarePlus className="mr-2 size-4" aria-hidden="true" />
				<span>Ny konversation</span>
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Starta en ny konversation?</DialogTitle>
						<DialogDescription>
							Den nuvarande konversationen rensas från skärmen. Du kan
							inte återgå till den.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setOpen(false)}>
							Avbryt
						</Button>
						<Button onClick={handleConfirm}>
							Starta ny konversation
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>,
		slot,
	)
}
